import { Helmet } from "react-helmet-async";
import { useI18n } from "@/lib/i18n";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  faq?: { question: string; answer: string }[];
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
}

const defaultTitle = "Car Age Calculator - How Old Is My Car?";
const defaultDescription =
  "Calculate a vehicle's exact age from its manufacturing date. Check years, months, days, milestone timing, and shareable results in one fast utility.";
const defaultKeywords = [
  "car age calculator",
  "vehicle age calculator",
  "car manufacturing date calculator",
  "vehicle manufacturing date calculator",
  "how old is my car",
  "car age by manufacturing date",
  "vehicle age checker",
  "RC age calculator",
  "vehicle date calculator",
  "car registration age checker",
  "mParivahan alternative",
  "Parivahan alternative",
  "RTO vehicle age calculator",
  "car age by date",
  "how to check car age",
  "vehicle registration age",
  "car milestone calculator",
];
const defaultOgImage = "/og-image.svg";
const defaultOgType = "website";
const defaultTwitterCard = "summary_large_image";

export const SEO = ({
  title = defaultTitle,
  description = defaultDescription,
  keywords = defaultKeywords,
  faq,
  ogImage = defaultOgImage,
  ogType = defaultOgType,
  twitterCard = defaultTwitterCard,
  canonicalUrl,
  noIndex = false,
}: SEOProps) => {
  const { locale } = useI18n();
  const siteUrl =
    import.meta.env.VITE_SITE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "https://car-age-calculator.com");
  const normalizedCanonical = canonicalUrl ? canonicalUrl : "/";
  const fullCanonicalUrl = `${siteUrl}${normalizedCanonical.startsWith("/") ? normalizedCanonical : `/${normalizedCanonical}`}`;
  const imageUrl = ogImage.startsWith("http") ? ogImage : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      <html lang={locale} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <meta name="author" content="Car Age Calculator" />
      <meta
        name="robots"
        content={
          noIndex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="application-name" content="Car Age Calculator" />
      <meta name="apple-mobile-web-app-title" content="Car Age Calculator" />
      <meta name="theme-color" content="#4f46e5" />
      <link rel="canonical" href={fullCanonicalUrl} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:site_name" content="Car Age Calculator" />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@vercel" />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebApplication",
              "name": "Car Age Calculator",
              "description": description,
              "url": fullCanonicalUrl,
              "inLanguage": "en",
              "applicationCategory": "UtilityApplication",
              "browserRequirements": "Requires JavaScript",
              "operatingSystem": "Any",
              "image": imageUrl,
              "keywords": keywords.join(", "),
              "audience": {
                "@type": "Audience",
                "audienceType": "Vehicle owners, car buyers, auto enthusiasts, and fleet managers",
              },
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD",
              },
              "potentialAction": {
                "@type": "UseAction",
                "target": fullCanonicalUrl,
              },
            },
            ...(faq && faq.length
              ? [
                  {
                    "@type": "FAQPage",
                    "mainEntity": faq.map((item) => ({
                      "@type": "Question",
                      "name": item.question,
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": item.answer,
                      },
                    })),
                  },
                ]
              : []),
          ],
        })}
      </script>
    </Helmet>
  );
}; 
